import React from 'react';

import { LegacyBox, LegacyText, TableCell } from '@gogame/components';

import {
  BudgetAmount,
  BudgetPercent,
  BudgetDaily,
  Metric,
  Comment,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/Fields';
import {
  NameCellBox,
  NameLinkStyled,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components';
import MetricApplyBelowControl from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/MetricApplyBelowControl';
import MetricCalculated from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/MetricCalculated';
import RemoveNode from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/RemoveNode';
import SplitBy from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/SplitBy';
import { BudgetAllocated } from 'scenes/MarketingPlanning/MarketingPlanningWizard/MarketingPlanningForm/components/ValueConsumers';
import {
  ActivityTypesIndicator,
  TableRowActivityTypes,
  TableCellDefaultBackground,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components';
import {
  GroupActivityType,
  GroupMetricsProvider,
  GroupCMetricsProvider,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Group';
import { useViewStateByResolveName } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/ViewState';
import { getMetricTypeById } from 'scenes/MarketingPlanning/utils';

import Actions from './Actions';

const Row = ({
  entityId,
  activityType,
  activityTypeIndex,
  activityTypeLength,
  withLinkName,
  withSplitBy,
  withRemove,
  withCBudgetEmpty,
  disabledMetricApplyBelow,
  levelName,
}) => {
  const { name, metricTypes } = useViewStateByResolveName();

  return (
    <TableRowActivityTypes label={name} type={activityType}>
      <GroupActivityType type={activityType}>
        {activityTypeIndex === 0 ? (
          <TableCellDefaultBackground
            rowSpan={activityTypeLength}
            label={`split-by_name_cell_${activityType}`}
          >
            <NameCellBox
              align={'center'}
              justifyContent={'space-between'}
              gutter={8}
            >
              {withLinkName ? (
                <NameLinkStyled to={`${entityId}`}>{name}</NameLinkStyled>
              ) : (
                <React.Fragment>
                  <LegacyText>{name}</LegacyText>
                  {withSplitBy ? <SplitBy /> : null}
                </React.Fragment>
              )}
            </NameCellBox>
          </TableCellDefaultBackground>
        ) : null}
        <TableCell padding={'none'} style={{ height: '1px' }}>
          <ActivityTypesIndicator type={activityType} />
        </TableCell>
        <TableCell
          align={'right'}
          label={`split-by_budget_allocated_${activityType}`}
        >
          {withCBudgetEmpty ? '-' : <BudgetAllocated />}
        </TableCell>
        <TableCell label={`split-by_budget_amount_${activityType}`}>
          <BudgetAmount />
        </TableCell>
        <TableCell label={`split-by_budget_percent_${activityType}`}>
          <BudgetPercent />
        </TableCell>
        <TableCell label={`split-by_budget_daily_${activityType}`}>
          <BudgetDaily />
        </TableCell>
        {metricTypes.map(({ id, kindId }, i) => {
          const typeField = getMetricTypeById(kindId);
          const isNumeric = typeField === 'number';
          return (
            <React.Fragment key={id}>
              <GroupMetricsProvider>
                <TableCell size={'small'} label={`split-by_metric_${i}`}>
                  <LegacyBox gutter={4}>
                    <Metric
                      name={id}
                      typeField={typeField}
                      width={isNumeric ? '108px' : '58px'}
                    />
                    {!isNumeric ? (
                      <MetricApplyBelowControl
                        metric={id}
                        typeField={typeField}
                        disabled={disabledMetricApplyBelow}
                      />
                    ) : null}
                  </LegacyBox>
                </TableCell>
              </GroupMetricsProvider>
              <GroupCMetricsProvider>
                <TableCell label={`split-by_metric_calculated_${i}`}>
                  <MetricCalculated metric={id} typeField={typeField} />
                </TableCell>
              </GroupCMetricsProvider>
            </React.Fragment>
          );
        })}
        <TableCell label={`split-by_comment`}>
          <Comment />
        </TableCell>
        <TableCell />
        <TableCell borderLeft={true} label={`split-by_actions`}>
          <Actions />
        </TableCell>
        {withRemove && activityTypeIndex === 0 ? (
          <TableCellDefaultBackground
            borderLeft={true}
            rowSpan={activityTypeLength}
            label={`split-by_remove_node_${activityType}`}
          >
            <RemoveNode levelName={levelName} />
          </TableCellDefaultBackground>
        ) : null}
      </GroupActivityType>
    </TableRowActivityTypes>
  );
};

export default Row;
